import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Gift, Wine, Camera, Flower2, PartyPopper } from 'lucide-react';

const Decorations = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" 
        data-testid="decorations-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(255, 107, 107, 0.75), rgba(238, 90, 111, 0.75)), url(https://customer-assets.emergentagent.com/job_family-cuisine/artifacts/edehr5xc_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-white drop-shadow-lg">Event Decorations</h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md">
            Transform Your Vision into Reality with Personalized Décor
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#8b4513] mb-4">Complete Decoration Services</h2>
          <p className="text-lg text-[#6b4423] leading-relaxed">
            From grand balloon arches to personalized finishing touches, we provide comprehensive decoration services 
            that bring your event vision to life. Every detail is customized to reflect your style, theme, and personality.
          </p>
        </div>
      </section>

      {/* Balloon Arches */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1707589338174-dc1ddc18945a?w=800&q=80" 
                alt="Colorful Balloon Arch"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#ff6b6b] to-[#ee5a6f] text-white mb-6 shadow-lg">
                <PartyPopper size={48} />
              </div>
              <h2 className="text-4xl font-bold text-[#8b4513] mb-3">Personalized Balloon Arches & Installations</h2>
              <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">Make a Grand Entrance</p>
              <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
              <p className="text-lg text-[#6b4423] mb-6">
                Transform your event space with stunning custom balloon arches, columns, garlands, and installations.
              </p>
              <h3 className="text-2xl font-bold text-[#8b4513] mb-4">Options & Pricing:</h3>
              <div className="space-y-3">
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Mini Arch/Garland (6-8 ft)</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$150-$250</p>
                </div>
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Standard Arch (10-12 ft)</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$300-$500</p>
                </div>
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Grand Arch (15-20 ft)</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$600-$900</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Candy Tables */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2 relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_family-cuisine/artifacts/hs7g24d3_image.png" 
                alt="Elegant Candy Table with Pink Theme and Shimmer Backdrop"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div className="lg:order-1">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#f093fb] to-[#f5576c] text-white mb-6 shadow-lg">
                <Gift size={48} />
              </div>
              <h2 className="text-4xl font-bold text-[#8b4513] mb-3">Candy Tables & Dessert Bars</h2>
              <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">Sweet Treats, Beautifully Displayed</p>
              <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
              <p className="text-lg text-[#6b4423] mb-6">
                Create an Instagram-worthy candy buffet with elegant displays and coordinated décor.
              </p>
              <h3 className="text-2xl font-bold text-[#8b4513] mb-4">Packages:</h3>
              <div className="space-y-4">
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Sweet Start</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$350-$500</p>
                  <p className="text-[#6b4423]">3-4 candy types, 50 guests, basic display</p>
                </div>
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Deluxe Dessert Bar</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$650-$900</p>
                  <p className="text-[#6b4423]">6-8 options, 100 guests, premium displays</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wine & Champagne Tables */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_jfrai-catering/artifacts/oad5pezf_image.png" 
                alt="Elegant Wine and Champagne Table Decoration"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#a8edea] to-[#fed6e3] text-white mb-6 shadow-lg">
                <Wine size={48} />
              </div>
              <h2 className="text-4xl font-bold text-[#8b4513] mb-3">Wine & Champagne Table Decorations</h2>
              <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">Elevate Your Bar Experience</p>
              <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
              <p className="text-lg text-[#6b4423] mb-6">
                Sophisticated bar setups from champagne walls to wine tasting stations.
              </p>
              <h3 className="text-2xl font-bold text-[#8b4513] mb-4">Styles:</h3>
              <div className="space-y-3">
                <div className="menu-item">
                  <h4 className="text-lg font-bold text-[#8b4513]">Champagne Wall</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$400-$700</p>
                </div>
                <div className="menu-item">
                  <h4 className="text-lg font-bold text-[#8b4513]">Rustic Wine Bar</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$350-$600</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Backdrops */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2 relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1758606137571-79fb4dd7d5a4?w=800&q=80" 
                alt="Personalized Photo Backdrop with Custom Design"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div className="lg:order-1">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#4facfe] to-[#00f2fe] text-white mb-6 shadow-lg">
                <Camera size={48} />
              </div>
              <h2 className="text-4xl font-bold text-[#8b4513] mb-3">Personalized Photo Backdrops</h2>
              <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">Picture-Perfect Memories</p>
              <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
              <p className="text-lg text-[#6b4423] mb-6">
                Custom backdrops featuring your names, event hashtag, or custom designs.
              </p>
              <h3 className="text-2xl font-bold text-[#8b4513] mb-4">Popular Designs:</h3>
              <div className="space-y-3">
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Step & Repeat (8x8 ft)</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$400-$700</p>
                </div>
                <div className="card border-l-4 border-[#d2691e]">
                  <h4 className="text-lg font-bold text-[#8b4513]">Floral Wall (8x8 ft)</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$800-$1,500</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floral Centerpieces */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #ffd93d 0%, #f7b731 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&q=80" 
                alt="Floral Arrangements"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#ffd93d] to-[#f7b731] text-white mb-6 shadow-lg">
                <Flower2 size={48} />
              </div>
              <h2 className="text-4xl font-bold text-[#8b4513] mb-3">Floral Arrangements & Centerpieces</h2>
              <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">Natural Beauty for Every Table</p>
              <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
              <p className="text-lg text-[#6b4423] mb-6">
                Fresh floral designs from minimalist modern to lush romantic arrangements.
              </p>
              <h3 className="text-2xl font-bold text-[#8b4513] mb-4">Styles:</h3>
              <div className="space-y-3">
                <div className="menu-item">
                  <h4 className="text-lg font-bold text-[#8b4513]">Classic Elegance</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$75-$150 per table</p>
                </div>
                <div className="menu-item">
                  <h4 className="text-lg font-bold text-[#8b4513]">Modern Minimalist</h4>
                  <p className="text-lg font-bold text-[#d2691e]">$40-$80 per table</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personalized Details */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2 relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1478146896981-b80fe463b330?w=800&q=80" 
                alt="Personalized Details"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div className="lg:order-1">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white mb-6 shadow-lg">
                <Sparkles size={48} />
              </div>
              <h2 className="text-4xl font-bold text-[#8b4513] mb-3">Personalized Details & Finishing Touches</h2>
              <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">The Details That Make It Yours</p>
              <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
              <p className="text-lg text-[#6b4423] mb-6">
                Custom signage, place cards, favors, and more personalized items.
              </p>
              <h3 className="text-2xl font-bold text-[#8b4513] mb-4">Includes:</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">•</span>
                  <span className="text-[#6b4423]">Welcome signs</span>
                </div>
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">•</span>
                  <span className="text-[#6b4423]">Table numbers</span>
                </div>
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">•</span>
                  <span className="text-[#6b4423]">Place cards</span>
                </div>
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">•</span>
                  <span className="text-[#6b4423]">Custom favors</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section text-center" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
        <h2 className="section-title text-white">Ready to Design Your Dream Event?</h2>
        <div className="decorative-line bg-white"></div>
        <p className="section-subtitle text-white">
          Let's create something extraordinary together!
        </p>
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Link to="/contact">
            <button className="btn-primary bg-white text-[#f5576c] hover:bg-[#ffecd2]">
              Schedule a Consultation
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Decorations;
